import type { Tweet } from "react-tweet/api";
import { tweetsApiResponseSchema } from "./tweets-api-schema";

export const TWEET_THREAD_BATCH_SIZE = 50;

export type DeferredTweetThreadItem = {
  tweetId: string;
  tweet: Tweet | null;
};

type TweetBatchResponse = {
  ok: boolean;
  json: () => Promise<unknown>;
};

export type TweetBatchFetcher = (params: {
  url: string;
}) => Promise<TweetBatchResponse>;

export function chunkTweetIdsForThread({
  tweetIds,
}: {
  tweetIds: string[];
}): string[][] {
  const chunks: string[][] = [];

  for (
    let index = 0;
    index < tweetIds.length;
    index += TWEET_THREAD_BATCH_SIZE
  ) {
    chunks.push(tweetIds.slice(index, index + TWEET_THREAD_BATCH_SIZE));
  }

  return chunks;
}

export async function fetchDeferredTweetThread({
  tweetIds,
  fetchBatch,
}: {
  tweetIds: string[];
  fetchBatch: TweetBatchFetcher;
}): Promise<DeferredTweetThreadItem[] | null> {
  if (tweetIds.length === 0) {
    return [];
  }

  const chunkedIds = chunkTweetIdsForThread({ tweetIds });
  const responses = await Promise.all(
    chunkedIds.map(async (ids) => {
      const response = await fetchBatch({
        url: `/api/tweets?ids=${encodeURIComponent(ids.join(","))}`,
      });
      const json = await response.json();
      const parsed = tweetsApiResponseSchema.safeParse(json);

      if (!(response.ok && parsed.success)) {
        return null;
      }

      return parsed.data.tweets;
    })
  );

  if (responses.some((response) => response === null)) {
    return null;
  }

  const tweets: DeferredTweetThreadItem[] = [];
  for (const response of responses) {
    if (response === null) {
      return null;
    }

    tweets.push(...response);
  }

  return tweets;
}
